import { Icon } from '@/components/Atoms/Icon/Icon';
import { NeutralButton } from '@/components/Atoms/Neutral/Neutral';
import ChevronLeft from '@/components/Icons/ChevronLeft';
import ChevronLeftDouble from '@/components/Icons/ChevronLeftDouble';
import ChevronRight from '@/components/Icons/ChevronRight';
import ChevronRightDouble from '@/components/Icons/ChevronRightDouble';
import { breakpoints } from '@/styles/media';
import { getDateStringForLocale, getMonthAndYearStringForLocale } from '@/utils/dateUtils';
import {
    addDays,
    getMonth,
    isAfter,
    isBefore,
    isSameDay,
    isSameMonth,
    isSameWeek,
    parseISO,
    setMonth,
    startOfMonth,
    startOfWeek,
} from 'date-fns';
import type { ButtonHTMLAttributes, FC } from 'react';
import type { FCl } from 'react-fc';
import styled, { css } from 'styled-components';

// returns an array of week -> day (including prev / next month items that fill out the week)
const getWeeks = (currentDate: Date): Date[][] => {
    // set the counter to the first day of the first week
    let counter = startOfWeek(startOfMonth(currentDate));
    const weeks = [];
    let currentWeek = null;
    let currentWeekData = [];

    // always 6 rows, even if the last row doesn't appear in the current month at all
    while (weeks.length < 6) {
        if (!currentWeek || !isSameWeek(currentWeek, counter)) {
            if (currentWeek) {
                weeks.push(currentWeekData);
            }
            currentWeekData = [];
            currentWeek = counter;
        }

        currentWeekData.push(counter);
        counter = addDays(counter, 1);
    }
    return weeks;
};

const shortLabelFormat = Intl.DateTimeFormat(undefined, {
    weekday: 'narrow',
});
const fullLabelFormat = Intl.DateTimeFormat(undefined, {
    weekday: 'long',
});

interface IProps {
    visibleDate: Date;
    setVisibleDate: (newDate: Date) => void;
    onDateSelect: (newDate?: Date) => void;
    max?: Date;
    min?: Date;
    disabled?: boolean;
    selectedRange?: {
        from: string; // ISO string
        to: string; // ISO string
    };
    selectedDate?: Date;
    includeForwardButtons?: boolean;
    includeBackwardButtons?: boolean;
}

export const Calendar: FCl<IProps> = ({
    className,
    visibleDate,
    setVisibleDate,
    onDateSelect,
    selectedRange,
    selectedDate,
    disabled,
    max,
    min,
    includeForwardButtons = true,
    includeBackwardButtons = true,
}) => {
    const prevMonth = setMonth(visibleDate, getMonth(visibleDate) - 1);
    const prevYear = setMonth(visibleDate, getMonth(visibleDate) - 12);
    const nextMonth = setMonth(visibleDate, getMonth(visibleDate) + 1);
    const nextYear = setMonth(visibleDate, getMonth(visibleDate) + 12);

    const weeks = getWeeks(visibleDate);

    const rangeFrom = selectedRange && parseISO(selectedRange.from);
    const rangeTo = selectedRange && parseISO(selectedRange.to);

    return (
        <StyledCalendar className={className}>
            <caption className="cds-t-label cds-t-label--b">
                <TableCaption>
                    {includeBackwardButtons && (
                        <CalendarButtons>
                            <CalendarButton
                                data-cds-sizing="compact"
                                className="cds-button cds-button--icon cds-button--tertiary"
                                type="button"
                                title={getMonthAndYearStringForLocale(prevYear)}
                                onClick={() => setVisibleDate(prevYear)}
                                disabled={disabled || (min && !isSameMonth(min, prevYear) && isBefore(prevYear, min))}
                            >
                                <Icon use={<ChevronLeftDouble />} />
                            </CalendarButton>
                            <CalendarButton
                                data-cds-sizing="compact"
                                className="cds-button cds-button--icon cds-button--tertiary"
                                type="button"
                                tabIndex={-1}
                                title={getMonthAndYearStringForLocale(prevMonth)}
                                onClick={() => setVisibleDate(prevMonth)}
                                disabled={disabled || (min && !isSameMonth(min, prevMonth) && isBefore(prevMonth, min))}
                            >
                                <Icon use={<ChevronLeft />} />
                            </CalendarButton>
                        </CalendarButtons>
                    )}
                    <CaptionLabel>{getMonthAndYearStringForLocale(visibleDate)}</CaptionLabel>
                    {includeForwardButtons && (
                        <CalendarButtons>
                            <CalendarButton
                                data-cds-sizing="compact"
                                className="cds-button cds-button--icon cds-button--tertiary"
                                type="button"
                                tabIndex={-1}
                                title={getMonthAndYearStringForLocale(nextMonth)}
                                onClick={() => setVisibleDate(nextMonth)}
                                disabled={disabled || (max && !isSameMonth(max, nextMonth) && isAfter(nextMonth, max))}
                            >
                                <Icon use={<ChevronRight />} />
                            </CalendarButton>
                            <CalendarButton
                                data-cds-sizing="compact"
                                className="cds-button cds-button--icon cds-button--tertiary"
                                type="button"
                                tabIndex={-1}
                                title={getMonthAndYearStringForLocale(nextYear)}
                                onClick={() => setVisibleDate(nextYear)}
                                disabled={disabled || (max && !isSameMonth(max, nextYear) && isAfter(nextYear, max))}
                            >
                                <Icon use={<ChevronRightDouble />} />
                            </CalendarButton>
                        </CalendarButtons>
                    )}
                </TableCaption>
            </caption>
            <thead>
                <tr>
                    {weeks[0].map((day) => (
                        <th className="cds-t-label cds-t-label--b" key={fullLabelFormat.format(day)}>
                            <abbr title={fullLabelFormat.format(day)}>{shortLabelFormat.format(day)}</abbr>
                        </th>
                    ))}
                </tr>
            </thead>
            <tbody>
                {weeks.map((week) => (
                    <tr key={week[0].toISOString()}>
                        {week.map((day) => {
                            let status: DayCellProps['status'];
                            if (isSameDay(day, selectedDate ?? 0)) {
                                status = 'current';
                            } else if (!isSameMonth(day, visibleDate)) {
                                status = 'outside';
                            } else if (
                                (rangeFrom && isSameDay(rangeFrom, day)) ||
                                (rangeTo && isSameDay(rangeTo, day))
                            ) {
                                status = 'range-boundary';
                            }
                            let rangeStatus: DayCellProps['rangeStatus'];
                            if (isSameMonth(day, visibleDate) && rangeFrom && isSameDay(rangeFrom, day)) {
                                rangeStatus = 'start';
                            } else if (isSameMonth(day, visibleDate) && rangeTo && isSameDay(rangeTo, day)) {
                                rangeStatus = 'end';
                            } else if (
                                isSameMonth(day, visibleDate) &&
                                rangeTo &&
                                rangeFrom &&
                                isAfter(day, rangeFrom) &&
                                isBefore(day, rangeTo)
                            ) {
                                rangeStatus = 'inside';
                            }

                            return (
                                <DayCell
                                    key={day.toISOString()}
                                    onDateSelect={onDateSelect}
                                    status={status}
                                    rangeStatus={rangeStatus}
                                    date={day}
                                    disabled={disabled || (min && isBefore(day, min)) || (max && isAfter(day, max))}
                                />
                            );
                        })}
                    </tr>
                ))}
            </tbody>
        </StyledCalendar>
    );
};

interface DayCellProps extends ButtonHTMLAttributes<HTMLButtonElement> {
    date: Date;
    status?: 'current' | 'outside' | 'range-boundary';
    rangeStatus?: 'start' | 'end' | 'inside';
    onDateSelect: (newDate: Date) => void;
}

const DayCell: FC<DayCellProps> = ({ date, status, rangeStatus, onDateSelect, ...rest }) => (
    <StyledTd $start={rangeStatus === 'start'} $end={rangeStatus === 'end'} $inside={rangeStatus === 'inside'}>
        <DayButton
            type="button"
            tabIndex={-1}
            aria-selected={status === 'current'}
            $boundary={status === 'range-boundary'}
            $outside={status === 'outside'}
            {...rest}
            onClick={() => onDateSelect(date)}
        >
            <abbr title={getDateStringForLocale(date)}>{date.getDate()}</abbr>
        </DayButton>
    </StyledTd>
);

const StyledTd = styled.td<{ $start: boolean; $end: boolean; $inside: boolean }>`
    ${({ $start }) =>
        $start &&
        css`
            background: linear-gradient(90deg, transparent 50%, var(--color-DateInput-day-inRange) 50%);
        `};
    ${({ $end }) =>
        $end &&
        css`
            background: linear-gradient(90deg, var(--color-DateInput-day-inRange) 50%, transparent 50%);
        `};
    ${({ $inside }) =>
        $inside &&
        css`
            background: var(--color-DateInput-day-inRange);
        `};
`;

const DayButton = styled(NeutralButton)<{ $boundary: boolean; $outside: boolean }>`
    display: inline-block;
    line-height: 2;
    text-align: center;
    min-height: 36px;
    aspect-ratio: 1;
    border-radius: 2rem;

    &:not(:disabled):hover {
        background: var(--color-body-accent);
        color: var(--cds-color-background);
    }

    &:disabled {
        cursor: not-allowed;

        abbr {
            opacity: 0.3;
        }
    }

    &[aria-selected='true'] {
        font-weight: bold;
        background: var(--color-body-accent);
        color: var(--cds-color-background);
    }

    ${({ $boundary }) =>
        $boundary &&
        css`
            font-weight: bold;
            background: var(--color-DateInput-day-rangeBoundary);
        `};
    ${({ $outside }) =>
        $outside &&
        css`
            abbr {
                opacity: 0.3;
            }
        `};
`;

const StyledCalendar = styled.table`
    display: none;
    ${breakpoints.L`display:table;`}
    width: 100%;
    border-collapse: separate;
    border-spacing: 0 2px;

    caption {
        caption-side: top;
        margin-bottom: var(--size-grid-unit);
    }

    abbr {
        text-decoration: none;
    }

    th,
    td {
        width: ${100 / 7}%; /* 7 days in a week */
        height: auto;
        vertical-align: middle;
        text-align: center;
        padding: 0;
    }
`;

const TableCaption = styled.span`
    display: flex;
    align-items: center;
    justify-content: space-between;
`;

const CalendarButtons = styled.span`
    display: flex;
`;

const CalendarButton = styled.button`
    width: 2em;
    height: 2em;
    padding: 0;
    text-align: center;

    &:disabled {
        cursor: not-allowed;
        opacity: 0.6;
    }
`;

const CaptionLabel = styled.span`
    text-transform: uppercase;
    font-weight: bold;
    font-variant-numeric: tabular-nums;
`;
